import React, { useRef } from "react";
import { Modal, Button } from "react-bootstrap";
import { FaPrint, FaTimes, FaFileInvoiceDollar } from "react-icons/fa";
import { useReactToPrint } from "react-to-print";
import LogoAlsum from "../../assets/ysb/logo_alsum_saja.png";
import LogoYsb from "../../assets/ysb/logo-ysb.png";

// Helper format rupiah
const formatRupiah = (number) => {
  if (number === null || number === undefined || isNaN(number)) return "Rp 0";
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(number);
};

// Helper angka terbilang dalam bahasa Indonesia
const terbilang = (angka) => {
  const bilangan = [
    "", "Satu", "Dua", "Tiga", "Empat", "Lima", "Enam", "Tujuh", "Delapan", "Sembilan", "Sepuluh", "Sebelas"
  ];
  const num = Math.floor(Math.abs(Number(angka) || 0));
  
  if (num < 12) return bilangan[num];
  if (num < 20) return terbilang(num - 10) + " Belas";
  if (num < 100) return terbilang(Math.floor(num / 10)) + " Puluh " + terbilang(num % 10);
  if (num < 200) return "Seratus " + terbilang(num - 100);
  if (num < 1000) return terbilang(Math.floor(num / 100)) + " Ratus " + terbilang(num % 100);
  if (num < 2000) return "Seribu " + terbilang(num - 1000);
  if (num < 1000000) return terbilang(Math.floor(num / 1000)) + " Ribu " + terbilang(num % 1000);
  if (num < 1000000000) return terbilang(Math.floor(num / 1000000)) + " Juta " + terbilang(num % 1000000);
  if (num < 1000000000000) return terbilang(Math.floor(num / 1000000000)) + " Miliar " + terbilang(num % 1000000000);
  return "";
};

// Helper format nilai kinerja persentase
const formatNilaiKinerja = (val) => {
  if (val === undefined || val === null || val === "") return "0%";
  if (typeof val === "string" && val.includes(",")) {
    val = val.replace(",", ".");
  }
  const num = Number(val);
  if (isNaN(num)) return `${val}%`;
  if (num > 0 && num <= 1) {
    return `${parseFloat((num * 100).toFixed(2))}%`;
  }
  return `${parseFloat(num.toFixed(2))}%`;
};

export default function SlipGajiModal({ show, handleClose, data }) {
  const printRef = useRef();

  const cleanFileName = data
    ? `Slip_Gaji_${(data.nama || "Guru").replace(/[^a-zA-Z0-9]/g, "_")}_${(data.periode || "").replace(/[^a-zA-Z0-9]/g, "_")}`
    : "Slip_Gaji";

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: cleanFileName,
    pageStyle: `
      @page {
        size: A4 portrait;
        margin: 8mm 8mm 8mm 8mm;
      }
      @media print {
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          color-adjust: exact !important;
        }
        body {
          background-color: #ffffff !important;
          margin: 0 !important;
          padding: 0 !important;
        }
        #printable-slip-gaji {
          width: 100% !important;
          max-width: 100% !important;
          box-shadow: none !important;
          border: none !important;
          padding: 0 !important;
          margin: 0 !important;
        }
      }
    `,
  });

  if (!data) return null;

  const ditransferNominal = data.ditransfer || (data.total_pendapatan - data.jumlah_potongan) || 0;

  return (
    <Modal show={show} onHide={handleClose} size="xl" centered scrollable>
      <Modal.Header closeButton style={{ backgroundColor: "#00487F", color: "white" }}>
        <Modal.Title style={{ fontSize: "16px", fontWeight: "600", display: "flex", alignItems: "center", gap: "8px" }}>
          <FaFileInvoiceDollar /> Slip Gaji - {data.nama || "Guru / Karyawan"} ({data.periode || "-"})
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ backgroundColor: "#f3f4f6", padding: "20px" }}>
        {/* Printable Area Container */}
        <div
          ref={printRef}
          id="printable-slip-gaji"
          style={{
            backgroundColor: "#ffffff",
            padding: "26px 30px",
            borderRadius: "8px",
            boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
            fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
            color: "#111827",
            maxWidth: "920px",
            margin: "0 auto",
          }}
        >
          {/* Header Kop Surat */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              borderBottom: "2.5px solid #00487F",
              paddingBottom: "12px",
              marginBottom: "16px",
            }}
          >
            <div style={{ marginRight: "16px" }}>
              <img src={LogoYsb} alt="Logo Ysb" style={{ width: "70px", height: "auto", objectFit: "contain" }} />
            </div>
            <div style={{ marginRight: "16px" }}>
              <img src={LogoAlsum} alt="Logo Al-Azhar" style={{ width: "70px", height: "auto", objectFit: "contain" }} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontWeight: "bold", color: "#00487F", fontSize: "19px", letterSpacing: "0.5px" }}>
                YAYASAN SYIAR BANGSA
              </h3>
              <p style={{ margin: "2px 0 0 0", fontSize: "12.5px", color: "#4b5563", fontWeight: "500" }}>
                Sekolah Islam Al Azhar Summarecon
              </p>
              <div
                style={{
                  display: "inline-block",
                  backgroundColor: "#e0f2fe",
                  color: "#0369a1",
                  padding: "2px 8px",
                  borderRadius: "4px",
                  fontWeight: "700",
                  fontSize: "12px",
                  marginTop: "4px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                SLIP GAJI
              </div>
            </div>
          </div>

          {/* Info Pegawai Grid (Revisi: 3 baris kiri, 3 baris kanan) */}
          <div
            style={{
              backgroundColor: "#f8fafc",
              border: "1px solid #cbd5e1",
              borderRadius: "6px",
              padding: "10px 16px",
              marginBottom: "14px",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", rowGap: "6px" }}>
              {/* Kolom Kiri */}
              <div style={{ flex: "1 1 48%", minWidth: "260px", fontSize: "12.5px" }}>
                <div style={{ display: "flex", marginBottom: "4px" }}>
                  <span style={{ width: "100px", color: "#64748b" }}>Nama</span>
                  <span style={{ fontWeight: "bold", color: "#0f172a" }}>: {data.nama || "-"}</span>
                </div>
                <div style={{ display: "flex", marginBottom: "4px" }}>
                  <span style={{ width: "100px", color: "#64748b" }}>NIP YPI</span>
                  <span style={{ fontWeight: "500", color: "#0f172a" }}>: {data.nip_ypi || "-"}</span>
                </div>
                <div style={{ display: "flex" }}>
                  <span style={{ width: "100px", color: "#64748b" }}>Periode</span>
                  <span style={{ fontWeight: "bold", color: "#00487F" }}>: {data.periode || "-"}</span>
                </div>
              </div>

              {/* Kolom Kanan */}
              <div style={{ flex: "1 1 48%", minWidth: "260px", fontSize: "12.5px" }}>
                <div style={{ display: "flex", marginBottom: "4px" }}>
                  <span style={{ width: "115px", color: "#64748b" }}>No. Rekening</span>
                  <span style={{ fontWeight: "bold", color: "#0f172a" }}>: {data.no_rek_bni || "-"} (BNI)</span>
                </div>
                <div style={{ display: "flex", marginBottom: "4px" }}>
                  <span style={{ width: "115px", color: "#64748b" }}>Unit / Sekolah</span>
                  <span style={{ fontWeight: "500", color: "#0f172a" }}>: {data.unit_sekolah || data.school?.school_name || data.ysb_school_id || "-"}</span>
                </div>
                <div style={{ display: "flex" }}>
                  <span style={{ width: "115px", color: "#64748b" }}>Tgl Transfer</span>
                  <span style={{ fontWeight: "500", color: "#0f172a" }}>: {data.tgl_transfer || data.transfer || "-"}</span>
                </div>
              </div>
            </div>
          </div>

          {/* 2 Kolom Penghasilan & Potongan (Flex layout) */}
          <div style={{ display: "flex", gap: "14px", width: "100%", fontSize: "12px", alignItems: "flex-start" }}>
            {/* Kolom Kiri: I. PENGHASILAN / PENDAPATAN */}
            <div style={{ flex: "1", minWidth: "0" }}>
              <div style={{ border: "1px solid #cbd5e1", borderRadius: "6px", overflow: "hidden" }}>
                <div
                  style={{
                    backgroundColor: "#e0f2fe",
                    padding: "6px 12px",
                    fontWeight: "bold",
                    color: "#0369a1",
                    borderBottom: "1px solid #cbd5e1",
                    fontSize: "12px",
                  }}
                >
                  I. PENGHASILAN / PENDAPATAN
                </div>
                <div style={{ padding: "8px 12px" }}>
                  {/* 1. GAJI & TUNJANGAN TETAP */}
                  <div style={{ fontWeight: "bold", color: "#1e293b", margin: "2px 0 4px 0" }}>
                    1. GAJI & TUNJANGAN TETAP
                  </div>
                  <table style={{ width: "100%", marginBottom: "4px" }}>
                    <tbody>
                      <tr>
                        <td style={{ padding: "2px 0", color: "#475569" }}>• Gaji Pokok</td>
                        <td style={{ textAlign: "right", fontWeight: "600" }}>{formatRupiah(data.gaji_pokok)}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: "2px 0", color: "#475569" }}>• Tunj. Pengabdian</td>
                        <td style={{ textAlign: "right" }}>{formatRupiah(data.tunj_pengabdian)}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: "2px 0", color: "#475569" }}>• Tunj. Keluarga</td>
                        <td style={{ textAlign: "right" }}>{formatRupiah(data.tunj_keluarga)}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: "2px 0", color: "#475569" }}>• Tunj. Yayasan</td>
                        <td style={{ textAlign: "right" }}>{formatRupiah(data.tunj_yayasan)}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: "2px 0", color: "#475569" }}>• Tunj. Jabatan</td>
                        <td style={{ textAlign: "right" }}>{formatRupiah(data.tunj_jabatan)}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: "2px 0", color: "#475569" }}>• Tunj. Daerah</td>
                        <td style={{ textAlign: "right" }}>{formatRupiah(data.tunj_daerah)}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: "2px 0", color: "#475569" }}>• Tunj. PPh 21</td>
                        <td style={{ textAlign: "right" }}>{formatRupiah(data.tunj_pph21)}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: "2px 0", color: "#475569" }}>• Tunj. BPJS Ket. Subsidi</td>
                        <td style={{ textAlign: "right" }}>{formatRupiah(data.tunj_bpjs_ket)}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: "2px 0", color: "#475569" }}>• Tunj. BPJS Kes. Subsidi</td>
                        <td style={{ textAlign: "right" }}>{formatRupiah(data.tunj_bpjs_kes)}</td>
                      </tr>
                      <tr style={{ borderTop: "1px dashed #cbd5e1", fontWeight: "bold", color: "#0369a1" }}>
                        <td style={{ padding: "4px 0" }}>Subtotal 1. GAJI</td>
                        <td style={{ textAlign: "right" }}>{formatRupiah(data.jumlah_gaji || data.rekap_gaji)}</td>
                      </tr>
                    </tbody>
                  </table>

                  {/* 2. INSENTIF & TUNJANGAN VARIABEL */}
                  <div style={{ fontWeight: "bold", color: "#1e293b", margin: "6px 0 4px 0" }}>
                    2. INSENTIF & TUNJANGAN VARIABEL
                  </div>
                  <table style={{ width: "100%", marginBottom: "4px" }}>
                    <tbody>
                      <tr>
                        <td style={{ padding: "2px 0", color: "#475569" }}>• UKK</td>
                        <td style={{ textAlign: "right" }}>{formatRupiah(data.ukk)}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: "2px 0", color: "#475569" }}>• Nilai Kinerja</td>
                        <td style={{ textAlign: "right", fontWeight: "500" }}>
                          {formatNilaiKinerja(data.nilai_kinerja)}
                        </td>
                      </tr>
                      <tr>
                        <td style={{ padding: "2px 0", color: "#475569" }}>• UKK Kinerja</td>
                        <td style={{ textAlign: "right" }}>{formatRupiah(data.ukk_kinerja)}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: "2px 0", color: "#475569" }}>• Uang Transport</td>
                        <td style={{ textAlign: "right" }}>{formatRupiah(data.u_transport)}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: "2px 0", color: "#475569" }}>• Uang Makan</td>
                        <td style={{ textAlign: "right" }}>{formatRupiah(data.u_makan)}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: "2px 0", color: "#475569" }}>• Insentif IDK / KJM</td>
                        <td style={{ textAlign: "right" }}>{formatRupiah(data.insentif_idk_kjm)}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: "2px 0", color: "#475569" }}>• Kord. Bidang</td>
                        <td style={{ textAlign: "right" }}>{formatRupiah(data.kord_bidang)}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: "2px 0", color: "#475569" }}>• Wali Kelas</td>
                        <td style={{ textAlign: "right" }}>{formatRupiah(data.wali_kelas)}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: "2px 0", color: "#475569" }}>• Ragam Tunj. Lainnya</td>
                        <td style={{ textAlign: "right" }}>{formatRupiah(data.ragam_tunj_lainnya)}</td>
                      </tr>
                      {data.pembina_ekskul_nom > 0 || data.pembina_ekskul_ket || data.pembina_sd_nom > 0 || data.pembina_sd_ket ? (
                        <tr>
                          <td style={{ padding: "2px 0", color: "#475569" }}>
                            • Pembina Ekskul: {data.pembina_ekskul_ket || data.pembina_sd_ket || "Pembina"}
                          </td>
                          <td style={{ textAlign: "right" }}>{formatRupiah(data.pembina_ekskul_nom || data.pembina_sd_nom)}</td>
                        </tr>
                      ) : null}
                      <tr>
                        <td style={{ padding: "2px 0", color: "#475569" }}>• Pendapatan Lainnya</td>
                        <td style={{ textAlign: "right" }}>{formatRupiah(data.pendapatan_lainnya || data.rekap_lainnya || 0)}</td>
                      </tr>
                      <tr style={{ borderTop: "1px dashed #cbd5e1", fontWeight: "bold", color: "#0369a1" }}>
                        <td style={{ padding: "4px 0" }}>Subtotal 2. INSENTIF</td>
                        <td style={{ textAlign: "right" }}>{formatRupiah(data.jumlah_insentif || data.rekap_insentif)}</td>
                      </tr>
                    </tbody>
                  </table>

                  {/* TOTAL PENDAPATAN */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      backgroundColor: "#f0fdf4",
                      border: "1px solid #86efac",
                      padding: "6px 10px",
                      borderRadius: "4px",
                      fontWeight: "bold",
                      color: "#166534",
                      marginTop: "6px",
                      fontSize: "12px",
                    }}
                  >
                    <span>TOTAL PENDAPATAN (A)</span>
                    <span>{formatRupiah(data.total_pendapatan)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Kolom Kanan: II. POTONGAN & DATA ABSENSI */}
            <div style={{ flex: "1", minWidth: "0" }}>
              {/* II. POTONGAN */}
              <div style={{ border: "1px solid #cbd5e1", borderRadius: "6px", overflow: "hidden", marginBottom: "12px" }}>
                <div
                  style={{
                    backgroundColor: "#fee2e2",
                    padding: "6px 12px",
                    fontWeight: "bold",
                    color: "#b91c1c",
                    borderBottom: "1px solid #cbd5e1",
                    fontSize: "12px",
                  }}
                >
                  II. POTONGAN
                </div>
                <div style={{ padding: "8px 12px" }}>
                  <table style={{ width: "100%", marginBottom: "4px" }}>
                    <tbody>
                      <tr>
                        <td style={{ padding: "2px 0", color: "#475569" }}>• BPJS Ketenagakerjaan</td>
                        <td style={{ textAlign: "right" }}>{formatRupiah(data.pot_ketenagakerjaan)}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: "2px 0", color: "#475569" }}>• BPJS Kesehatan</td>
                        <td style={{ textAlign: "right" }}>{formatRupiah(data.pot_kesehatan)}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: "2px 0", color: "#475569" }}>• Infaq Pegawai</td>
                        <td style={{ textAlign: "right" }}>{formatRupiah(data.pot_infaq_pegawai || data.pot_zakat)}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: "2px 0", color: "#475569" }}>• Koperasi</td>
                        <td style={{ textAlign: "right" }}>{formatRupiah(data.pot_koperasi)}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: "2px 0", color: "#475569" }}>• Potongan UKK</td>
                        <td style={{ textAlign: "right" }}>{formatRupiah(data.pot_ukk || data.pot_absensi)}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: "2px 0", color: "#475569" }}>• Potongan WFH Transport</td>
                        <td style={{ textAlign: "right" }}>{formatRupiah(data.pot_wfh_transport)}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: "2px 0", color: "#475569" }}>• PPh 21</td>
                        <td style={{ textAlign: "right" }}>{formatRupiah(data.pot_pph21)}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: "2px 0", color: "#475569" }}>• BPJS Ket. Subsidi</td>
                        <td style={{ textAlign: "right" }}>{formatRupiah(data.pot_bpjs_ket_subsidi)}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: "2px 0", color: "#475569" }}>• BPJS Kes. Subsidi</td>
                        <td style={{ textAlign: "right" }}>{formatRupiah(data.pot_bpjs_kes_subsidi)}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: "2px 0", color: "#475569" }}>• Infaq Masjid</td>
                        <td style={{ textAlign: "right" }}>{formatRupiah(data.pot_infaq_masjid)}</td>
                      </tr>
                    </tbody>
                  </table>

                  {/* TOTAL POTONGAN */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      backgroundColor: "#fef2f2",
                      border: "1px solid #fca5a5",
                      padding: "6px 10px",
                      borderRadius: "4px",
                      fontWeight: "bold",
                      color: "#991b1b",
                      marginTop: "6px",
                      fontSize: "12px",
                    }}
                  >
                    <span>TOTAL POTONGAN (B)</span>
                    <span>{formatRupiah(data.jumlah_potongan || data.rekap_potongan)}</span>
                  </div>
                </div>
              </div>

              {/* DATA ABSENSI (Persis sesuai catatan koreksi PDF) */}
              <div style={{ border: "1px solid #cbd5e1", borderRadius: "6px", overflow: "hidden" }}>
                <div
                  style={{
                    backgroundColor: "#f1f5f9",
                    padding: "5px 12px",
                    fontWeight: "bold",
                    color: "#334155",
                    borderBottom: "1px solid #cbd5e1",
                    fontSize: "11.5px",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  DATA ABSENSI
                </div>
                <div style={{ padding: "6px 12px" }}>
                  <table style={{ width: "100%", fontSize: "11.5px" }}>
                    <tbody>
                      <tr>
                        <td style={{ padding: "1.5px 0", color: "#475569" }}>• Terlambat -5</td>
                        <td style={{ textAlign: "right", fontWeight: "600", color: "#0f172a" }}>{data.att_terlambat_kurang_5 ?? 0}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: "1.5px 0", color: "#475569" }}>• Terlambat +5</td>
                        <td style={{ textAlign: "right", fontWeight: "600", color: "#0f172a" }}>{data.att_terlambat_lebih_5 ?? 0}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: "1.5px 0", color: "#475569" }}>• Pulang Cepat -5</td>
                        <td style={{ textAlign: "right", fontWeight: "600", color: "#0f172a" }}>{data.att_pulang_kurang_5 ?? 0}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: "1.5px 0", color: "#475569" }}>• Pulang Cepat +5</td>
                        <td style={{ textAlign: "right", fontWeight: "600", color: "#0f172a" }}>{data.att_pulang_lebih_5 ?? 0}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: "1.5px 0", color: "#475569" }}>• Absen 1x</td>
                        <td style={{ textAlign: "right", fontWeight: "600", color: "#0f172a" }}>{data.att_absen_1x ?? 0}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: "1.5px 0", color: "#475569" }}>• Hadir Kerja</td>
                        <td style={{ textAlign: "right", fontWeight: "600", color: "#0f172a" }}>{data.att_hadir_kerja ?? 0}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: "1.5px 0", color: "#475569" }}>• Hadir Libur</td>
                        <td style={{ textAlign: "right", fontWeight: "600", color: "#0f172a" }}>{data.att_hadir_libur ?? 0}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: "1.5px 0", color: "#475569" }}>• Jml Hadir</td>
                        <td style={{ textAlign: "right", fontWeight: "600", color: "#0f172a" }}>{data.att_jml_hadir ?? 0}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: "1.5px 0", color: "#475569" }}>• Tidak Hadir</td>
                        <td style={{ textAlign: "right", fontWeight: "600", color: "#0f172a" }}>{data.att_tidak_hadir ?? 0}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* DITRANSFER / TAKE HOME PAY BANNER */}
          <div
            style={{
              backgroundColor: "#00487F",
              color: "white",
              padding: "12px 18px",
              borderRadius: "8px",
              marginTop: "12px",
              marginBottom: "12px",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "1px", opacity: 0.9 }}>
                  PENGHASILAN BERSIH / DITRANSFER (A - B)
                </div>
                <div style={{ fontSize: "22px", fontWeight: "bold", marginTop: "2px" }}>
                  {formatRupiah(ditransferNominal)}
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <span
                  style={{
                    backgroundColor: "rgba(255,255,255,0.2)",
                    padding: "4px 12px",
                    borderRadius: "20px",
                    fontSize: "11.5px",
                    fontWeight: "600",
                  }}
                >
                  Bank Transfer (BNI)
                </span>
              </div>
            </div>
            {ditransferNominal > 0 && (
              <div
                style={{
                  borderTop: "1px solid rgba(255,255,255,0.2)",
                  marginTop: "6px",
                  paddingTop: "5px",
                  fontSize: "11px",
                  fontStyle: "italic",
                }}
              >
                Terbilang: {terbilang(ditransferNominal)} Rupiah
              </div>
            )}
          </div>

          {/* Footer Note */}
          <div style={{ marginTop: "14px", fontSize: "11px", color: "#475569" }}>
            <p style={{ margin: 0, fontStyle: "italic" }}>
              * Catatan : Bukti ini dicetak oleh komputerisasi, oleh karena itu tidak membutuhkan tanda tangan.
            </p>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer style={{ backgroundColor: "#ffffff" }}>
        <Button variant="secondary" onClick={handleClose} style={{ borderRadius: "6px", fontSize: "13px" }}>
          <FaTimes className="me-1" /> Tutup
        </Button>
        <Button
          onClick={handlePrint}
          style={{
            backgroundColor: "#00487F",
            borderColor: "#00487F",
            borderRadius: "6px",
            fontSize: "13px",
            fontWeight: "500",
          }}
        >
          <FaPrint className="me-1" /> Cetak / Print Slip
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
